import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useRef, useState } from 'react';
import SignedImage from '@/components/ipe/SignedImage';
import { sheinPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';
import { withRequiredMark } from '@/shein-platform/components/RequiredMark';
import SheinUploadSourceMenu from '@/shein-platform/components/SheinUploadSourceMenu';
import LibraryModalContent from '@/pages/ipe/workbench/modals/LibraryModalContent';
import {
  convertImageSpaceToPublishSlot,
  imageSpaceRecordToUploadFile,
  withImageSpaceDurableUrl,
} from '@/shein-platform/utils/sheinImageSpacePick';
import { appendUniqueImageSpaceRows } from '@/pages/ipe/workbench/utils/imageSpacePageFetch';
import '@/styles/ipe/workbench/IpeModalUnified.css';
import {
  SHEIN_MAIN_DETAIL_MIN,
  SHEIN_MAIN_HEIGHT,
  SHEIN_MAIN_WIDTH,
  SHEIN_PRODUCT_IMAGE_ACCEPT,
  SHEIN_PRODUCT_IMAGE_HINT,
  SHEIN_PRODUCT_IMAGE_MAX_BYTES,
  prepareSheinProductImage,
  validateSheinProductImageFormat,
} from '@/shein-platform/utils/sheinProductImage';
import { useFixedAspectCropGate } from '@/components/image/useFixedAspectCropGate';
import SheinImagePreviewLightbox from '@/shein-platform/modules/products/components/SheinImagePreviewLightbox';
import { isLikelyBrowserReachableThumb, pickBrowserPreviewImageUrl } from '@/shein-platform/modules/products/components/SheinProductThumb';
import { isSignedViewPath } from '@/utils/imageDisplayAccess';
import {
  resolveUploadThumbSrc,
  shouldUseSignedThumb,
  toCanonicalUploadsPath,
} from '@/shein-platform/modules/products/components/publishImageUploadThumbSrc';

function PublishUploadThumb({ item }) {
  const src = resolveUploadThumbSrc(item);
  // 裂图时不要用文件名当 alt，否则灰框里铺满哈希名
  const alt = '';
  if (!src) {
    return <span className="spo-img-upload__thumb-empty" aria-hidden="true" />;
  }
  if (!shouldUseSignedThumb(src)) {
    return (
      <img
        src={src}
        alt={alt}
        loading="eager"
        decoding="async"
        referrerPolicy="no-referrer"
        draggable={false}
      />
    );
  }
  return (
    <SignedImage
      url={src}
      alt={alt}
      loading="eager"
      className="spo-img-upload__signed"
      fallback={<span className="spo-img-upload__thumb-empty" aria-hidden="true" />}
    />
  );
}

function buildUploadedSlot(file, res, { requireSheinUrl = true, skipBlobPreview = false } = {}) {
  const sheinUrl = String(res?.sheinUrl || res?.url || '').trim();
  if (requireSheinUrl && !sheinUrl) {
    throw new Error(res?.message || res?.msg || chartText('publishImg.noSheinUrl', '图片上传未返回 SHEIN 地址，请重试'));
  }
  const rawIpe = String(res?.ipeUrl || res?.originalUrl || res?.storedUrl || '').trim();
  const rawPublic = String(
    res?.publicUrl
    || res?.previewUrlDisplay
    || res?.originalUrlDisplay
    || res?.viewUrl
    || res?.displayUrl
    || '',
  ).trim();
  // 持久化只存 canonical /uploads/…，禁止把跨端口绝对 uploads 写进表单（刷新后必裂）
  const durable = toCanonicalUploadsPath(rawIpe)
    || toCanonicalUploadsPath(rawPublic)
    || rawIpe
    || rawPublic
    || '';
  // 店铺发品：同页 blob 立刻出图。官方模板必须走 OSS 签名地址，换电脑才能看见，禁止只停在浏览器 blob。
  let previewUrl = '';
  if (!skipBlobPreview) {
    try {
      if (typeof URL !== 'undefined' && file) {
        previewUrl = URL.createObjectURL(file);
      }
    } catch {
      /* ignore */
    }
  }
  if (!previewUrl) {
    const fallback = pickBrowserPreviewImageUrl(rawPublic, durable, rawIpe, sheinUrl);
    previewUrl = (fallback && isLikelyBrowserReachableThumb(fallback)) ? fallback : durable;
  }
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: file.name,
    previewUrl,
    publicUrl: isSignedViewPath(rawPublic) ? rawPublic : durable,
    ipeUrl: durable,
    sheinUrl,
  };
}

async function uploadLocalWithoutSheinConvert(file) {
  const res = await adminAPI.storeOfficialSheinProductTemplateImage(file);
  const slot = buildUploadedSlot(file, res, { requireSheinUrl: false, skipBlobPreview: true });
  if (!slot.ipeUrl && !slot.publicUrl && !slot.previewUrl) {
    throw new Error(res?.message || res?.msg || chartText('publishImg.noUrl', '图片上传未返回地址，请重试'));
  }
  return slot;
}

async function uploadOne(file, shopId, imageType = 1, {
  skipSheinConvert = false,
  /** true=已在图片空间，只转 SHEIN；false=本机文件（点击/拖入），写入图片空间+OSS 并转 SHEIN */
  fromImageSpace = false,
} = {}) {
  if (skipSheinConvert) {
    return uploadLocalWithoutSheinConvert(file);
  }
  if (!shopId) {
    throw new Error(chartText('publishImg.pickShopFirst', '请先选择店铺后再上传图片'));
  }
  const res = await sheinPlatformAPI.uploadProductPic(shopId, file, {
    imageType,
    fromImageSpace: fromImageSpace === true,
  });
  return buildUploadedSlot(file, res, { requireSheinUrl: true });
}

/** 发品图片上传：单图占满槽位；多图可配置上传格固定在最前、新图插到最前；多图可拖拽调序。默认按 SHEIN 商品图规则校验并压缩。 */
export default function PublishImageUploadZone({
  label,
  hint,
  value = [],
  multiple = false,
  maxCount = 9,
  shopId,
  onChange,
  compact = false,
  /** 标题放在上传框内部（对齐商家后台商品图槽位） */
  labelInside = false,
  round = false,
  /** 是否启用 SHEIN 商品图尺寸/格式/3MB 规则（默认开启） */
  enforceSheinRules = true,
  /**
   * 官方产品模板无店铺：跳过 SHEIN URL 转图，只落 IPE 原图（仍走 3:4 比例校验/裁剪）。
   */
  skipSheinConvert = false,
  /**
   * SHEIN image_type：1主图 2细节图 7详情图（方块/色块由发品提交时后端按主图派生）
   */
  imageType = 1,
  /** 多图：上传格固定在最前面 */
  uploadSlotFirst = false,
  /** 多图：新上传图片插到列表最前（紧贴上传格） */
  prependNew = false,
  /** 多图：支持拖拽调整缩略图顺序 */
  reorderable = false,
  /** 是否允许从图片空间选图（默认开启） */
  enableImageSpacePick = true,
}) {
  useTranslation('workbench');
  const inputRef = useRef(null);
  const uploadAnchorRef = useRef(null);
  const dragIndexRef = useRef(-1);
  const imageSpaceRowsRef = useRef([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(-1);
  const [dropTargetIndex, setDropTargetIndex] = useState(-1);
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [librarySelection, setLibrarySelection] = useState([]);
  const list = Array.isArray(value) ? value : (value ? [value] : []);
  const displayHint = hint || (enforceSheinRules && !compact && !labelInside ? chartText('publishImg.hintProductShein', SHEIN_PRODUCT_IMAGE_HINT) : '');
  const canUploadMore = multiple ? list.length < maxCount : list.length === 0;
  const canReorder = Boolean(multiple && reorderable && list.length > 1);
  const remainSlots = multiple ? Math.max(0, maxCount - list.length) : (list.length === 0 ? 1 : 0);
  const { ensureAspectFile, cropModal, cropping } = useFixedAspectCropGate({
    title: chartText('publishImg.cropCarouselTitle', '裁剪为 3:4 商品图'),
    hint: chartText('publishImg.cropCarouselHintShein', '主图/细节图须为 3:4，且宽高均在 900–2200px（推荐 1340×1785）。请拖动裁剪框选择要保留的区域。'),
    outputWidth: SHEIN_MAIN_WIDTH,
    outputHeight: SHEIN_MAIN_HEIGHT,
    maxBytes: SHEIN_PRODUCT_IMAGE_MAX_BYTES,
    minCropEdge: SHEIN_MAIN_DETAIL_MIN,
  });

  const prepareUploadFile = useCallback(async (raw) => {
    if (!enforceSheinRules) return raw;
    const formatError = validateSheinProductImageFormat(raw);
    if (formatError) throw new Error(formatError);
    const aspectOk = await ensureAspectFile(raw);
    // 官方模板也做 3:4 规范化；店铺发品同样缩放到平台像素
    return prepareSheinProductImage(aspectOk, { requireAspect: true });
  }, [enforceSheinRules, ensureAspectFile]);

  const rememberImageSpaceRows = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    imageSpaceRowsRef.current = appendUniqueImageSpaceRows(imageSpaceRowsRef.current, rows);
  }, []);

  const applyFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;

    const remain = multiple ? Math.max(0, maxCount - list.length) : 1;
    const picked = files.slice(0, remain);
    if (picked.length === 0) {
      showToast(chartText('publishImg.maxCount', `最多上传 ${maxCount} 张`, { count: maxCount }), 'warning');
      return;
    }

    setUploading(true);
    try {
      const uploaded = [];
      for (const raw of picked) {
        const file = await prepareUploadFile(raw);
        // 本机点击/拖入：与「本地上传」一致，落图片空间 + OSS + SHEIN
        const main = await uploadOne(file, shopId, imageType, {
          skipSheinConvert,
          fromImageSpace: false,
        });
        uploaded.push(main);
      }
      if (multiple) {
        const next = prependNew
          ? [...uploaded, ...list]
          : [...list, ...uploaded];
        onChange?.(next.slice(0, maxCount));
      } else {
        onChange?.(uploaded[0] || null);
      }
      showToast(uploaded.length > 1 ? chartText('publishImg.uploadedMany', `已上传 ${uploaded.length} 张`, { count: uploaded.length }) : chartText('publishImg.uploaded', '图片已上传'), 'success');
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg.includes('已取消裁剪')) {
        showToast(chartText('publishImg.cropCancelled', '已取消裁剪，未上传该图片'), 'info');
      } else {
        showToast(msg || chartText('publishImg.uploadFail', '图片上传失败'), 'error', Math.min(12000, Math.max(5000, msg.length * 60)));
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const applyImageSpaceRecords = async (records) => {
    const picked = (Array.isArray(records) ? records : []).filter(Boolean);
    if (picked.length === 0) return;
    const remain = multiple ? Math.max(0, maxCount - list.length) : 1;
    const batch = picked.slice(0, remain);
    if (batch.length === 0) {
      showToast(chartText('publishImg.maxCount', `最多上传 ${maxCount} 张`, { count: maxCount }), 'warning');
      return;
    }
    if (batch.length < picked.length) {
      showToast(chartText('publishImg.overLimit', `超出上限，仅处理前 ${batch.length} 张`, { count: batch.length }), 'warning');
    }

    setUploading(true);
    try {
      const uploaded = [];
      for (const image of batch) {
        if (enforceSheinRules) {
          const rawFile = await imageSpaceRecordToUploadFile(image);
          const file = await prepareUploadFile(rawFile);
          uploaded.push(withImageSpaceDurableUrl(await uploadOne(file, shopId, imageType, {
            skipSheinConvert,
            fromImageSpace: true,
          }), image));
        } else {
          uploaded.push(await convertImageSpaceToPublishSlot({
            shopId,
            image,
            imageType,
            skipSheinConvert,
          }));
        }
      }
      if (multiple) {
        const next = prependNew
          ? [...uploaded, ...list]
          : [...list, ...uploaded];
        onChange?.(next.slice(0, maxCount));
      } else {
        onChange?.(uploaded[0] || null);
      }
      showToast(uploaded.length > 1 ? chartText('publishImg.pickedMany', `已选用 ${uploaded.length} 张`, { count: uploaded.length }) : chartText('publishImg.picked', '图片已选用'), 'success');
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg.includes('已取消裁剪')) {
        showToast(chartText('publishImg.cropCancelPick', '已取消裁剪，未选用该图片'), 'info');
      } else {
        showToast(msg || chartText('photoLib.pickFailed', '图片选用失败'), 'error', Math.min(12000, Math.max(5000, msg.length * 60)));
      }
    } finally {
      setUploading(false);
    }
  };

  const openUploadMenu = () => {
    if (uploading || cropping) return;
    if (enableImageSpacePick) {
      setUploadMenuOpen(true);
      return;
    }
    inputRef.current?.click();
  };

  const handleLibraryClose = () => {
    setLibrarySelection([]);
    setLibraryOpen(false);
  };

  const handleLibraryToggle = (imageId) => {
    const id = String(imageId);
    setLibrarySelection((prev) => {
      if (prev.some((x) => String(x) === id)) {
        return prev.filter((x) => String(x) !== id);
      }
      if (!multiple) return [id];
      const limit = remainSlots > 0 ? remainSlots : maxCount;
      if (prev.length >= limit) {
        showToast(chartText('photoLib.remainMax', `最多再选 ${limit} 张`, { count: limit }), 'warning');
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleLibraryConfirm = async (positionArray) => {
    const ids = (positionArray || []).filter((x) => x != null && x !== '');
    if (ids.length === 0) {
      showToast(chartText('photoLib.pickOne', fixedUi("请至少选择一张图片")), 'info');
      return;
    }
    const limit = multiple ? (remainSlots > 0 ? remainSlots : maxCount) : 1;
    const pickedIds = ids.slice(0, limit);
    const records = pickedIds
      .map((id) => imageSpaceRowsRef.current.find((row) => String(row?.id) === String(id)))
      .filter(Boolean);
    if (records.length === 0) {
      showToast(chartText('photoLib.invalidImages', fixedUi("所选图片无效或已失效，请重试")), 'warning');
      return;
    }
    handleLibraryClose();
    await applyImageSpaceRecords(records);
  };

  const removeAt = (id) => {
    if (multiple) onChange?.(list.filter((item) => item.id !== id));
    else onChange?.(null);
  };

  const moveItem = (fromIndex, toIndex) => {
    if (!canReorder) return;
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
    if (fromIndex >= list.length || toIndex >= list.length) return;
    const next = list.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    onChange?.(next);
  };

  const clearReorderState = () => {
    dragIndexRef.current = -1;
    setDraggingIndex(-1);
    setDropTargetIndex(-1);
  };

  const uploadButton = canUploadMore ? (
    <button
      ref={uploadAnchorRef}
      type="button"
      className={`spo-img-upload__drop${dragOver ? ' spo-img-upload__drop--over' : ''}${uploading ? ' spo-img-upload__drop--busy' : ''}`}
      disabled={uploading || cropping}
      aria-busy={uploading || cropping}
      aria-haspopup={enableImageSpacePick ? 'menu' : undefined}
      onClick={openUploadMenu}
      onDragEnter={(e) => {
        if (dragIndexRef.current >= 0) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragOver={(e) => {
        if (dragIndexRef.current >= 0) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (dragIndexRef.current >= 0) {
          clearReorderState();
          return;
        }
        applyFiles(e.dataTransfer?.files);
      }}
    >
      {uploading || cropping ? (
        <span className="spo-img-upload__busy" aria-live="polite">
          <span className="spo-img-upload__spinner" aria-hidden="true" />
          <span className="spo-img-upload__busy-text">{cropping ? chartText('publishImg.cropping', fixedUi("裁剪中")) : chartText('photoLib.uploading', fixedUi("上传中"))}</span>
        </span>
      ) : labelInside && label ? (
        <>
          <span className="spo-img-upload__slot-label">{withRequiredMark(label)}</span>
          <span className="spo-img-upload__slot-action">{chartText('photoLib.clickUpload', fixedUi("点击上传"))}</span>
        </>
      ) : (
        <>
          <span className="spo-img-upload__plus">+</span>
          <span>{chartText('photoLib.clickUpload', fixedUi("点击上传"))}</span>
          {!compact ? <span className="spo-img-upload__sub">{displayHint || SHEIN_PRODUCT_IMAGE_HINT}</span> : null}
        </>
      )}
    </button>
  ) : null;

  const thumbNodes = list.map((item, index) => (
    <div
      key={item.id || `thumb-${index}`}
      className={[
        'spo-img-upload__thumb',
        canReorder ? 'spo-img-upload__thumb--reorderable' : '',
        draggingIndex === index ? 'is-dragging' : '',
        dropTargetIndex === index ? 'is-drop-target' : '',
      ].filter(Boolean).join(' ')}
      draggable={canReorder}
      title={canReorder ? chartText('publishImg.reorder', '拖拽调整顺序') : undefined}
      onDragStart={(e) => {
        if (!canReorder) return;
        if (e.target.closest('.spo-img-upload__remove, .spo-img-upload__preview')) {
          e.preventDefault();
          return;
        }
        dragIndexRef.current = index;
        setDraggingIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
        try {
          e.dataTransfer.setData('application/x-ipe-shein-img-reorder', String(index));
        } catch {
          /* ignore */
        }
      }}
      onDragOver={(e) => {
        if (!canReorder || dragIndexRef.current < 0) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dropTargetIndex !== index) setDropTargetIndex(index);
      }}
      onDragLeave={() => {
        if (dropTargetIndex === index) setDropTargetIndex(-1);
      }}
      onDrop={(e) => {
        if (!canReorder) return;
        e.preventDefault();
        e.stopPropagation();
        const from = dragIndexRef.current;
        clearReorderState();
        moveItem(from, index);
      }}
      onDragEnd={() => {
        clearReorderState();
      }}
    >
      <PublishUploadThumb item={item} />
      <button
        type="button"
        className="spo-img-upload__remove"
        aria-label={chartText('publishImg.remove', '移除图片')}
        onClick={() => removeAt(item.id)}
      >
        ×
      </button>
      <button
        type="button"
        className="spo-img-upload__preview"
        onClick={() => setPreviewItem(item)}
      >
        {fixedUi("预览")}
      </button>
    </div>
  ));

  return (
    <div className={`spo-img-upload${compact ? ' spo-img-upload--compact' : ''}${labelInside ? ' spo-img-upload--label-inside' : ''}${round ? ' spo-img-upload--round' : ''}`}>
      {(label || displayHint) && !labelInside ? (
        <div className="spo-img-upload__head">
          {label ? <span className="spo-field__label">{withRequiredMark(label)}</span> : null}
          {displayHint ? <span className="spo-img-upload__hint">{displayHint}</span> : null}
        </div>
      ) : null}

      <div className="spo-img-upload__grid">
        {uploadSlotFirst ? (
          <>
            {uploadButton}
            {thumbNodes}
          </>
        ) : (
          <>
            {thumbNodes}
            {uploadButton}
          </>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={(enforceSheinRules || skipSheinConvert) ? SHEIN_PRODUCT_IMAGE_ACCEPT : 'image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp'}
        multiple={multiple}
        hidden
        onChange={(e) => applyFiles(e.target.files)}
      />

      <SheinImagePreviewLightbox
        src={previewItem ? resolveUploadThumbSrc(previewItem) : ''}
        alt={previewItem?.name || chartText('publishSpec.preview', fixedUi("预览"))}
        preferSigned={Boolean(previewItem && shouldUseSignedThumb(resolveUploadThumbSrc(previewItem)))}
        onClose={() => setPreviewItem(null)}
      />

      {enableImageSpacePick ? (
        <>
          <SheinUploadSourceMenu
            open={uploadMenuOpen}
            anchorRef={uploadAnchorRef}
            onClose={() => setUploadMenuOpen(false)}
            disabled={uploading || cropping}
            onPickLocal={() => inputRef.current?.click()}
            onPickImageSpace={() => setLibraryOpen(true)}
          />
          <LibraryModalContent
            isOpen={libraryOpen}
            onClose={handleLibraryClose}
            images={[]}
            selection={librarySelection}
            onToggle={handleLibraryToggle}
            maxSelection={multiple ? (remainSlots > 0 ? remainSlots : maxCount) : 1}
            onConfirm={handleLibraryConfirm}
            onImagesAppended={rememberImageSpaceRows}
            uploadOriginSource="upload"
            showToast={showToast}
            deferUsageBump
            selectAllLabel={chartText('photoLib.selectLoaded', '全选已加载')}
            closeOnBackdropClick={false}
          />
        </>
      ) : null}
      {cropModal}
    </div>
  );
}
